import { authApi } from '@/api/AuthApi'
import type { LoginFormData } from '@/components/LoginPage/LoginForm/LoginForm.types'
import { JoinLine } from '@/components/LoginPage/LoginWelcome'
import { AppleMark, GoogleMark } from '@/components/svgs/ProviderMarks'
import {
	LoginCodeStatus,
	authUserFromCustomToken,
	sendLoginCodeToEmail,
} from '@/services/auth'
import { Button, ButtonText } from '@/ui/core/button'
import { Header } from '@/ui/core/header'
import { Input } from '@/ui/core/input'
import { HStack, VStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import { valibotResolver } from '@hookform/resolvers/valibot'
import { router } from 'expo-router'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { View } from 'react-native'
import Toast from 'react-native-toast-message'
import { email, minLength, object, pipe, string, trim } from 'valibot'

const LoginSchema = object({
	emailAddress: pipe(
		string(),
		trim(),
		email('Login must be a valid email'),
		minLength(1)
	),
})

const AppReviewEmail = 'appreview@hiwally.co'

const AccountNotFoundMessage = `We couldn't find that email address in our system. Please use the email address you signed up with when creating your membership.`

const RateLimitedMessage =
	'You have made too many requests in a short period of time. Please wait a few minutes before trying again.'

// TODO(auth): Sign in with Apple and Google. The buttons are drawn and placed; the flows behind
// them (provider auth, then linking the identity to a membership) are not built yet.
const continueWithApple = () => {}
const continueWithGoogle = () => {}

/** "or continue with email", between two hairlines the colour of a field's edge. */
const OrDivider = ({ label }: { label: string }) => (
	<HStack gap={12} className="items-center">
		<View className="h-px flex-1 bg-edge" />
		<Text size="body2" tone="soft">
			{label}
		</Text>
		<View className="h-px flex-1 bg-edge" />
	</HStack>
)

const UnsubmittedLoginForm = ({ onSubmit = () => {} }) => {
	const [isSendingLoginCode, setIsSendingLoginCode] = useState(false)

	const {
		control,
		handleSubmit,
		formState: { isValid, errors },
	} = useForm<LoginFormData>({
		resolver: valibotResolver(LoginSchema),
	})

	const onLoginSubmit = async ({ emailAddress }: LoginFormData) => {
		if (!isValid) {
			Toast.show({
				type: 'error',
				text1: errors.emailAddress?.message,
			})
		}
		setIsSendingLoginCode(true)

		if (emailAddress === AppReviewEmail) {
			const customToken = await authApi.fetchDemoAccountToken()

			if (customToken) {
				await authUserFromCustomToken(customToken.token, () => {
					router.replace('/')
				})
				setIsSendingLoginCode(false)
				return
			}
		}

		const loginCodeStatus = await sendLoginCodeToEmail(emailAddress)
		setIsSendingLoginCode(false)

		if (loginCodeStatus === LoginCodeStatus.RATE_LIMITED) {
			Toast.show({
				type: 'error',
				text1: RateLimitedMessage,
			})
		}

		if (loginCodeStatus === LoginCodeStatus.NOT_FOUND) {
			Toast.show({
				type: 'error',
				text1: AccountNotFoundMessage,
			})
		}

		if (loginCodeStatus === LoginCodeStatus.SUCCESSFUL) {
			onSubmit()
		}
	}

	const submit = handleSubmit(onLoginSubmit)

	return (
		<VStack className="w-full" gap={20}>
			<VStack gap={4} className="mb-6">
				<Header variant="h3" className="text-[40px] leading-[40px]">
					Log In
				</Header>
				<Text tone="soft">Use the account linked to your membership.</Text>
			</VStack>

			<VStack gap={12}>
				<Button
					size="lg"
					fullWidth
					onPress={continueWithApple}
					accessibilityLabel="Continue with Apple"
				>
					<HStack gap={10} className="items-center">
						<AppleMark />
						<ButtonText>Continue with Apple</ButtonText>
					</HStack>
				</Button>
				<Button
					variant="secondary"
					size="lg"
					fullWidth
					onPress={continueWithGoogle}
					accessibilityLabel="Continue with Google"
				>
					<HStack gap={10} className="items-center">
						<GoogleMark />
						<ButtonText>Continue with Google</ButtonText>
					</HStack>
				</Button>
			</VStack>

			<OrDivider label="or continue with email" />

			<VStack gap={12}>
				<Controller
					name="emailAddress"
					control={control}
					render={({ field: { onChange, onBlur, value } }) => (
						<Input
							placeholder="Email"
							accessibilityLabel="Email"
							value={value}
							onBlur={onBlur}
							onChangeText={onChange}
							inputMode="email"
							autoComplete="email"
							autoCapitalize="none"
							autoCorrect={false}
							returnKeyType="go"
							onSubmitEditing={() => isValid && submit()}
							errorText={errors.emailAddress?.message}
						/>
					)}
				/>
				<Button
					size="lg"
					fullWidth
					isLoading={isSendingLoginCode}
					disabled={!isValid}
					onPress={submit}
				>
					<ButtonText>Continue</ButtonText>
				</Button>
			</VStack>

			<JoinLine tone="soft" />
		</VStack>
	)
}

export { UnsubmittedLoginForm }
